import React, { useState } from "react";
import { useForm } from "react-hook-form";
import styles from "./ForgotPassword.module.scss";
import { api } from "../../services/api";
import { ApiError } from "../../services/api/BaseApi";

interface ForgotPasswordForm {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordForm>();

  const onSubmit = async (data: ForgotPasswordForm) => {
    setError(null);
    try {
      await api.app.forgotPassword(data.email.toLowerCase());
      setSubmitted(true);
    } catch (err: any) {
      if (err instanceof ApiError) {
        setError(err.data?.message || err.message || "Something went wrong.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <span className={styles.cursive}>Hippocampus Gaming</span>
          <p className={styles.tagline}>Your gaming universe, all in one place</p>
        </div>

        <div className={styles.divider}>Forgot password</div>

        {submitted ? (
          <div className={styles.successMessage}>
            <p>Check your email for a password reset link.</p>
            <p className={styles.hint}>
              If you don't see it, check your spam folder.
            </p>
            <a href="/signin" className={styles.backLink}>
              Back to sign in
            </a>
          </div>
        ) : (
          <>
            <p className={styles.description}>
              Enter the email address associated with your account and we'll send
              you a link to reset your password.
            </p>

            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
              <div className={styles.inputGroup}>
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  style={{ fontSize: "16px" }}
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Invalid email format",
                    },
                  })}
                />
                {errors.email && (
                  <span className={styles.error}>{errors.email.message}</span>
                )}
              </div>

              {error && <span className={styles.error}>{error}</span>}

              <button
                type="submit"
                className={styles.button}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Send reset link"}
              </button>
            </form>

            <p className={styles.footer}>
              Remember your password? <a href="/signin">Sign in</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
